import { getGoalsAndInterventionsByCertificationPeriodOptions, type InterventionViewModel } from '@generated';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { DataTable, type DataTableProps } from '~/shared/data-tables/DataTable';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '~/shared/components/modal/Modal';
import { TableActions } from '~/shared/data-tables/TableActions';

function RenderActionsCell(props: InterventionViewModel) {
	return (
		<TableActions>
			<TableActions.ViewButton
				aria-label="View Goals"
				renderModal={closeModal => <ViewGoalsModal intervention={props} closeModal={closeModal} />}
			/>
		</TableActions>
	);
}

function ViewGoalsModal({ intervention, closeModal }: { intervention: InterventionViewModel; closeModal: () => void }) {
	const { data: goalsAndInterventions, isLoading } = useQuery(
		getGoalsAndInterventionsByCertificationPeriodOptions(intervention.certificationPeriodId)
	);
	if (!goalsAndInterventions || isLoading) {
		return null;
	}
	const interventionGoals = goalsAndInterventions.goals.filter(g => g.interventionIds.includes(intervention.id)) ?? [];

	return (
		<Modal title="Goals" closeModal={closeModal} isDismissable onOpenChange={isOpen => !isOpen && closeModal()}>
			{isLoading ? (
				<p className="text-sm text-gray-500">Loading goals…</p>
			) : interventionGoals.length === 0 ? (
				<p className="text-sm text-gray-500">No goals are associated with this intervention.</p>
			) : (
				<div className="space-y-2">
					{interventionGoals.map(goal => (
						<div key={goal.id} className="rounded-lg border border-gray-200 px-4 py-3">
							<p className="text-sm font-medium text-gray-900">{goal.definitionText}</p>
						</div>
					))}
				</div>
			)}
		</Modal>
	);
}

const columns: DataTableColumnProps<InterventionViewModel>[] = [
	{
		header: 'Intervention',
		key: 'interventionText',
		renderCell: d => d.definitionText
	},
	{
		header: 'Assigned Disciplines',
		key: 'assignedDisciplines',
		renderCell: d => d.assignedDisciplines.map(ad => ad.assignedDisciplineAbbreviation).join(', ')
	},
	{
		header: 'Type',
		key: 'interventionTypeLabel',
		renderCell: d => d.interventionTypeLabel
	},
	{
		header: 'Start Date',
		key: 'projectedStartDate',
		renderCell: d => d.projectedStartDate.toLocaleDateString()
	},
	{
		key: 'actions',
		header: 'Actions',
		width: 'w-24',
		renderCell: d => <RenderActionsCell {...d} />
	}
];

export function InterventionTable({
	rows,
	...props
}: { rows: InterventionViewModel[] } & Omit<DataTableProps<InterventionViewModel>, 'columns' | 'rows' | 'getRowNotes'>) {
	const sorted = rows.toSorted((a, b) => {
		if (a.interventionTypeId !== b.interventionTypeId) {
			return a.interventionTypeId! - b.interventionTypeId!;
		}
		return a.projectedStartDate.getTime() - b.projectedStartDate.getTime();
	});
	return <DataTable columns={columns} rows={sorted} getRowNotes={x => x.notes ?? null} {...props} />;
}
